import { useBetslipStore } from '@/store/betslip';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { BetslipBetIcon, Icon, QuickbetIcon } from '@/components/icons/svg-icons';
import { useThrottleClick } from '@nf/utils-common/hooks';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { KafkaActionSettings, KafkaGalaxyTypeEnums, KafkaMarsTypeEnums, KafkaNote } from '@nf/utils-common/constants';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { addCommasWithFix } from '@/utils/odds-utils';
import { useTranslations } from 'next-intl';
import { BetModeControlStyle } from './bet-mode-control.style';
import clsx from 'clsx';
import { useAnalyseAttribute } from '@/features/next-analyse';

export const BetModeControl = ({
	type = 'betMode'
}: {
	type: 'betMode' | 'quickBet';
}) => {
	const t = useTranslations('General');
	const view = useAppearanceStore(state => state.view);
	const [
		isBookingBet,
		isQuickBet,
		enableQuickBet,
		useSabaCoin,
		toggleIsQuickBet,
		setIsQuickBetSettingModalOpen
	] = useBetslipStore(state => [
		state.isBookingBet,
		state.isQuickBet,
		state.enableQuickBet,
		state.useSabaCoin,
		state.toggleIsQuickBet,
		state.setIsQuickBetSettingModalOpen
	]);

	const isSwitchDisabled = !enableQuickBet || isBookingBet || useSabaCoin;
	const { currencyName, currencySymbol, quickBetStake } = useBetslipContext();
	const analyseAttributeRef = useAnalyseAttribute('toggle-quick-bet-from-betslip');

	const showToastMessage = (msg: string) => {
		document.dispatchEvent(new CustomEvent('show-toast-msg', { detail: { msg } }));
	};

	const onHandleClickToggleIsQuickBet = () => {
		setKafka(isQuickBet);
		const currencyStake = `${currencyName} ${currencySymbol}${addCommasWithFix(quickBetStake)}`;
		const messages = {
			beenSetMsg: view === ViewEnum.mars ? t('lbl_OneClickBetX', { 0: currencyStake }) : t('lbl_SetQBetX', { 0: currencyStake }),
			disabledMsg: view === ViewEnum.mars ? t('lbl_DisabledOneClickBet') : t('lbl_DisabledQBet')
		};
		if (!isQuickBet) {
			if (quickBetStake > 0) {
				showToastMessage(messages.beenSetMsg);
			} else {
				setIsQuickBetSettingModalOpen(true);
				return; 
			}
		}
		if (isQuickBet) {
			showToastMessage(messages.disabledMsg);
		}
		toggleIsQuickBet();
	};

	const setKafka = (isQuickBet: boolean) => {
		const isGalaxy = view === ViewEnum.galaxy;
		const action = isGalaxy ? (isQuickBet ? KafkaActionSettings.GalaxyV2[KafkaGalaxyTypeEnums.BetSlip].QuickBetOff : KafkaActionSettings.GalaxyV2[KafkaGalaxyTypeEnums.BetSlip].QuickBetOn) : (isQuickBet ? KafkaActionSettings.MarsV1[KafkaMarsTypeEnums.BetSlip].ChangeToDefaultBet : KafkaActionSettings.MarsV1[KafkaMarsTypeEnums.BetSlip].ChangeToOneClickBet);
		if (action) {
			const kafkaData = {
				note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1,
				type: isGalaxy ? 8 : 10,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const clickSwitch = useThrottleClick(onHandleClickToggleIsQuickBet);

	return (
		<>
			{
				{ 'betMode': (
					<div
						ref={analyseAttributeRef}
						className="quickbet-switch"
						data-active={isQuickBet}
						data-disabled={isSwitchDisabled}
						onClick={isSwitchDisabled ? undefined : clickSwitch}
					>
						<div className="quickbet-switch__thumb quickbet-switch__thumb--normal">
							<BetslipBetIcon />
						</div>
						<div className="quickbet-switch__thumb quickbet-switch__thumb--quick">
							<QuickbetIcon />
						</div>
					</div>
				),
				'quickBet': (
					<div
						className={clsx('quickbet-switch', BetModeControlStyle.quickbetSwitch)}
						data-active={isQuickBet}
						data-disabled={isSwitchDisabled}
						onClick={isSwitchDisabled ? undefined : clickSwitch}
					>
						<div data-active={!isQuickBet} className={clsx('quickbet-switch__thumb', BetModeControlStyle.leftSwitch)}>
							<Icon name="quickbet" />
						</div>
						<div data-active={isQuickBet} className={clsx('quickbet-switch__thumb', BetModeControlStyle.rightSwitch)}>
							<Icon name="quickbet" />
						</div>
					</div>
				) }[type]
			}
		</>
	)
};
